/* ==========================================================================
   AUGUST ONLINE — homepage behaviour
   No dependencies. IntersectionObserver + rAF only.
   Safe to load once per page; every routine is idempotent and re-runs on
   Shopify's `shopify:section:load` so the theme editor stays in sync.
   ========================================================================== */
(function () {
  'use strict';

  // Every section that needs this file loads it, so the same <script>
  // can appear a dozen times on one page. Browsers cache the download
  // but execute each tag, which would give us duplicate listeners,
  // duplicate modals and double cart adds. Run once per page.
  if (window.__augustHomeLoaded) return;
  window.__augustHomeLoaded = true;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------------------------------------------------------------------
     Scroll reveal
     --------------------------------------------------------------------- */
  var revealObserver = null;

  function getRevealObserver() {
    if (revealObserver || !('IntersectionObserver' in window)) return revealObserver;

    revealObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('au-visible');
          revealObserver.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.08 }
    );

    return revealObserver;
  }

  function initReveal(scope) {
    var nodes = (scope || document).querySelectorAll('[data-au-reveal]:not([data-au-reveal-bound])');
    var observer = getRevealObserver();

    nodes.forEach(function (node, index) {
      node.setAttribute('data-au-reveal-bound', '');

      if (!observer || reduceMotion.matches) {
        node.classList.add('au-visible');
        return;
      }

      // Stagger siblings so grids resolve as a sequence, not a flash.
      if (!node.style.getPropertyValue('--au-delay')) {
        node.style.setProperty('--au-delay', Math.min(index, 6) * 70 + 'ms');
      }

      observer.observe(node);
    });
  }

  /* ---------------------------------------------------------------------
     Header: wrap the header group so it can float over the hero
     --------------------------------------------------------------------- */
  var headbar = null;

  function buildHeadbar() {
    if (document.querySelector('.au-headbar')) {
      headbar = document.querySelector('.au-headbar');
      return headbar;
    }

    var groups = document.querySelectorAll('.shopify-section-group-header-group');
    if (!groups.length) return null;

    var first = groups[0];
    if (!first.parentNode) return null;

    headbar = document.createElement('div');
    headbar.className = 'au-headbar';
    first.parentNode.insertBefore(headbar, first);

    Array.prototype.forEach.call(groups, function (group) {
      headbar.appendChild(group);
    });

    return headbar;
  }

  function measureHeader() {
    if (!headbar) return;
    var height = headbar.getBoundingClientRect().height;
    document.documentElement.style.setProperty('--au-header-h', Math.round(height) + 'px');
  }

  function initHeader() {
    var hero = document.querySelector('[data-au-hero][data-au-overlap="true"]');
    if (!hero) return;

    // The August header section manages its own float/stick state and simply
    // publishes its height. Only fall back to wrapping Dawn's stock header
    // group when that section is not installed.
    var augustHeader = document.querySelector('[data-au-header]');
    if (augustHeader) {
      headbar = augustHeader;
      measureHeader();
      if (window.ResizeObserver) new ResizeObserver(measureHeader).observe(augustHeader);
      else window.addEventListener('resize', measureHeader);
      return;
    }

    if (!buildHeadbar()) return;

    document.documentElement.classList.add('au-transparent-header');
    measureHeader();

    if (window.ResizeObserver) {
      new ResizeObserver(measureHeader).observe(headbar);
    } else {
      window.addEventListener('resize', measureHeader);
    }
  }

  /* ---------------------------------------------------------------------
     Scroll loop: header state + hero parallax
     --------------------------------------------------------------------- */
  var ticking = false;

  function onScrollFrame() {
    ticking = false;

    var y = window.pageYOffset || document.documentElement.scrollTop;

    if (headbar && headbar.classList.contains('au-headbar')) {
      headbar.classList.toggle('au-headbar--stuck', y > 24);
    }

    if (!reduceMotion.matches) {
      var media = document.querySelector('[data-au-parallax]');
      if (media) {
        var hero = media.closest('[data-au-hero]');
        var limit = hero ? hero.offsetHeight : 0;
        if (y < limit) {
          media.style.transform = 'translate3d(0,' + (y * 0.18).toFixed(2) + 'px,0)';
        }
      }
    }
  }

  function requestFrame() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(onScrollFrame);
  }

  /* ---------------------------------------------------------------------
     Horizontal product rails: arrow controls + edge state
     --------------------------------------------------------------------- */
  function initRails(scope) {
    var rails = (scope || document).querySelectorAll('[data-au-rail]:not([data-au-rail-bound])');

    rails.forEach(function (rail) {
      rail.setAttribute('data-au-rail-bound', '');

      var section = rail.closest('[data-au-section]') || document;
      var prev = section.querySelector('[data-au-rail-prev]');
      var next = section.querySelector('[data-au-rail-next]');
      if (!prev && !next) return;

      function step(direction) {
        var item = rail.querySelector('li');
        var distance = item ? item.getBoundingClientRect().width + 16 : rail.clientWidth * 0.8;
        rail.scrollBy({ left: distance * direction, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      }

      function syncEdges() {
        var maxScroll = rail.scrollWidth - rail.clientWidth - 2;
        if (prev) prev.disabled = rail.scrollLeft <= 2;
        if (next) next.disabled = rail.scrollLeft >= maxScroll;
      }

      if (prev) prev.addEventListener('click', function () { step(-1); });
      if (next) next.addEventListener('click', function () { step(1); });
      rail.addEventListener('scroll', syncEdges, { passive: true });
      window.addEventListener('resize', syncEdges);
      syncEdges();
    });
  }

  /* ---------------------------------------------------------------------
     Boot
     --------------------------------------------------------------------- */
  function init(scope) {
    initReveal(scope);
    initRails(scope);
  }

  function boot() {
    document.documentElement.classList.remove('no-js');
    initHeader();
    init(document);
    requestFrame();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  window.addEventListener('scroll', requestFrame, { passive: true });
  window.addEventListener('resize', requestFrame);

  // Theme editor support
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
    measureHeader();
    requestFrame();
  });

  document.addEventListener('shopify:section:unload', measureHeader);
})();
